import React, { FC, useMemo, useState, useEffect } from 'react'
import { Box } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import Button from 'UI/Button/ButtonWithPreloader'
import { ReactComponent as StartIcon } from 'assets/svg/PlustSquareIcon.svg'
import Paper from '@material-ui/core/Paper/Paper'
import IconButton from '@material-ui/core/IconButton'
import {
  ReactComponent as ArrowLeftIcon,
  ReactComponent as ArrowLeft,
} from 'assets/svg/ArrowLeftIcon.svg'
import { generatePath, RouteComponentProps, useHistory, useParams } from 'react-router-dom'
import { useAssignLendersStyle } from 'containers/Deal/AssignLenders/style'
import cn from 'classnames'
import { TableLenders, useAssignLenders } from 'hooks/useAssignLenders'
import { useMutation, useQuery, useQueryCache } from 'react-query'
import { createLender, getDeal, getLenderList, updateLendersCumulative } from 'services/deals'
import { getSyndicator } from 'services/syndicators'
import ModalComponent from 'UI/Modal'
import AssignLenderForm from 'components/AssignLenderForm'
import { useSnackbar } from 'notistack'
import { SOMETHING_WENT_WRONG } from 'constants/errors'
import { AxiosError } from 'axios'
import ConfirmationContent from 'UI/Modal/ConfirmationContent'
import { DEAL_INFO_URL, NEW_DEAL_URL } from 'constants/routes'
import { DealsStatus } from 'typescript/enums/deals'
import { CompanyType } from 'typescript/interfaces/companies'
import { ILenderParams, ILenderEntity, ILendersCumulativeUpdate } from 'typescript/interfaces/deals'
import { ASSIGN_LENDERS_APPROVED_PERM } from 'constants/permissions'
import { usePermission } from 'hooks/usePermission'
import { ISyndicatorDetailEntity } from 'typescript/interfaces/syndicators'
import { convertToPercentageFormat } from 'utils/formatters'
import makeStyles from '@material-ui/core/styles/makeStyles'

const useAlertStyles = makeStyles(theme => ({
  alert: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    border: '1px solid',
  },
  success: {
    backgroundColor: theme.palette.success.light || '#e7f2e6',
    borderColor: theme.palette.success.main || '#5CAA5B',
    color: '#000000',
  },
  warning: {
    backgroundColor: theme.palette.warning.light || '#FFF3CD',
    borderColor: theme.palette.warning.main || '#DCAC00',
    color: '#000000',
  },
  info: {
    backgroundColor: theme.palette.info.light || '#e3f2fd',
    borderColor: theme.palette.info.main || '#2196f3',
    color: theme.palette.info.dark || '#1976d2',
  },
  alertTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}))

export interface AssignLendersProps extends RouteComponentProps {
  dealActions?: boolean
}

export interface ILenderModalState extends ILenderParams {
  lenderId: number
  syndicatorBalance?: number
}

const AssignLendersContainer: FC<AssignLendersProps> = ({ dealActions }) => {
  const { id, view } = useParams<{ id: string; type: 'inhouse' | 'outhouse'; view: string }>()
  const { hasPermission } = usePermission()

  const [lenders, setLenders] = useState<ILenderEntity[]>([])
  const [originalLenders, setOriginalLenders] = useState<ILenderEntity[]>([])
  const [isValidating, setIsValidating] = useState(false)

  const initValue: ILenderModalState = {
    lenderId: 0,
    dealId: id,
    syndicatorId: '',
    lendingPercentage: '',
    lendingAmount: '',
    isLead: false,
  }

  const { data: dataDealInfo } = useQuery(['inhouse-deal', id], () => getDeal(id))
  const { goBack, push } = useHistory()
  const classes = useAssignLendersStyle()
  const { enqueueSnackbar } = useSnackbar()
  const queryCache = useQueryCache()

  const [modalAssignState, setModalAssignState] = useState({
    type: 'new',
    open: false,
    initValue: initValue,
  })

  const [modalRemoveLenderState, setModalRemoveLenderState] = useState({
    open: false,
    id: 0,
    idCompany: 0,
  })

  const activeLenderActions = useMemo(() => {
    return (
      dataDealInfo &&
      ((dataDealInfo.data.status !== DealsStatus.declined &&
        dataDealInfo.data.status !== DealsStatus.approved) ||
        hasPermission(ASSIGN_LENDERS_APPROVED_PERM))
    )
  }, [dataDealInfo, hasPermission])

  const { columns, popover } = useAssignLenders({
    disableActions: !activeLenderActions,
    handleEdit: data => {
      setModalAssignState({
        type: 'edit',
        open: true,
        initValue: {
          lenderId: data.id,
          dealId: id,
          syndicatorId: data.syndicatorId,
          lendingPercentage: data.lendingPercentage.toString(),
          lendingAmount: data.lendingAmount,
          isLead: data.isLead,
          syndicatorBalance: data.availableBalance,
        },
      })
    },
    handleDelete: (idLender, idCompany) => {
      setModalRemoveLenderState({
        id: idLender,
        open: true,
        idCompany: idCompany,
      })
    },
  })

  const { data, isLoading } = useQuery(['getLendersList', id], () => getLenderList(id), {
    retry: false,
    cacheTime: 0,
  })

  useEffect(() => {
    const lendersData = data?.data || []
    setLenders(lendersData)
    setOriginalLenders(lendersData)
  }, [data])

  // Calculate participation statistics
  const participationStats = useMemo(() => {
    const totalPercentage = lenders.reduce(
      (sum, lender) => sum + Number(lender.lendingPercentage),
      0
    )
    const totalAmount = lenders.reduce((sum, lender) => sum + Number(lender.lendingAmount), 0)
    const leadLenders = lenders.filter(lender => lender.isLead)
    const hasLeadLender = leadLenders.length > 0
    const hasOnlyOneLeadLender = leadLenders.length === 1
    const isFullyParticipated = Math.abs(totalPercentage - 100) < 0.01 // Allow for small rounding differences
    const availablePercentage = Math.max(0, 100 - totalPercentage)
    const availableAmount = Math.max(0, (dataDealInfo?.data.advanceAmount || 0) - totalAmount)

    return {
      totalPercentage,
      totalAmount,
      hasLeadLender,
      hasOnlyOneLeadLender,
      leadLendersCount: leadLenders.length,
      isFullyParticipated,
      availablePercentage,
      availableAmount,
      canSave: isFullyParticipated && hasOnlyOneLeadLender && lenders.length > 0,
    }
  }, [lenders, dataDealInfo])

  const addLenderModal = async (lenderData: ILenderParams) => {
    // Check if lender already exists
    const existingLender = lenders.find(
      (el: ILenderEntity) => el.syndicatorId === lenderData?.syndicatorId
    )

    if (existingLender) {
      return enqueueSnackbar('Lender already exists in this deal')
    }

    // Check if adding this lender would exceed 100%
    const newPercentage = Number(lenderData.lendingPercentage)
    if (participationStats.totalPercentage + newPercentage > 100.01) {
      return enqueueSnackbar('Adding this lender would exceed 100% participation')
    }

    // Check if trying to add a main lender when one already exists
    if (lenderData.isLead && participationStats.hasLeadLender) {
      return enqueueSnackbar('Only one main lender is allowed per deal')
    }

    try {
      const syndicatorData = await getSyndicator(lenderData.syndicatorId.toString())
      const preparedData: ISyndicatorDetailEntity = syndicatorData?.data

      const newLenderData: ILenderEntity = {
        availableBalance: Number(preparedData.userPage.availableBalance),
        businessName: preparedData.businessName,
        id: Number(lenderData.lenderId),
        lenderId: Number(lenderData.lenderId),
        syndicatorId: Number(lenderData.syndicatorId),
        lendingPercentage: Number(lenderData.lendingPercentage),
        lendingAmount: Number(lenderData.lendingAmount),
        firstName: preparedData.firstName || '',
        lastName: preparedData.lastName || '',
        paybackAmount: 0,
        assigningDate: new Date().toISOString(),
        isLead: lenderData.isLead,
      }

      setLenders(prev => [...prev, newLenderData])

      setModalAssignState({
        type: 'new',
        open: false,
        initValue: initValue,
      })

      enqueueSnackbar('Lender added successfully')
    } catch (error) {
      enqueueSnackbar('Failed to add lender. Please try again.')
    }
  }

  const editLenderModal = (lenderData: ILenderParams) => {
    // Check if editing would exceed 100%
    const currentLender = lenders.find(
      (el: ILenderEntity) => el.syndicatorId === lenderData?.syndicatorId
    )

    if (currentLender) {
      const newPercentage = Number(lenderData.lendingPercentage)
      const otherLendersTotal = participationStats.totalPercentage - currentLender.lendingPercentage

      if (otherLendersTotal + newPercentage > 100.01) {
        return enqueueSnackbar('Editing this lender would exceed 100% participation')
      }

      // Check if trying to set as main lender when another main lender already exists
      if (lenderData.isLead && participationStats.hasLeadLender && !currentLender.isLead) {
        return enqueueSnackbar('Only one main lender is allowed per deal')
      }

      // Update the lender directly in the list
      setLenders(prev =>
        prev.map(lender =>
          lender.syndicatorId === lenderData?.syndicatorId
            ? {
                ...lender,
                lendingPercentage: Number(lenderData.lendingPercentage),
                lendingAmount: Number(lenderData.lendingAmount),
                isLead: lenderData.isLead,
              }
            : lender
        )
      )
      enqueueSnackbar('Lender updated successfully')
    }

    setModalAssignState({
      type: 'new',
      open: false,
      initValue: initValue,
    })
  }

  const deleteLenderModal = (lenderData: { companyId: number; lenderId: number }) => {
    // Remove the lender from the list
    setLenders(prev => prev.filter(lender => lender.syndicatorId !== lenderData?.companyId))
    setModalRemoveLenderState({
      idCompany: 0,
      id: 0,
      open: false,
    })
    enqueueSnackbar('Lender removed successfully')
  }

  const [, { isLoading: isLoadingAddLender }] = useMutation(createLender)

  const [updateLendersCumulativeMut, { isLoading: isLoadingCumulativeUpdate }] = useMutation(
    updateLendersCumulative,
    {
      onSuccess: () => {
        queryCache.fetchQuery(['getLendersList', id], () => getLenderList(id))
        queryCache.invalidateQueries(['inhouse-deal', id])
        enqueueSnackbar('Lenders updated successfully')
        setIsValidating(false)
        // Update original lenders after successful save
        setOriginalLenders(lenders)
      },
      onError: (err: AxiosError<{ description?: string }>) => {
        const description = err.response?.data?.description
        if (description === 'INVALID_LENDING_PERCENTAGE') {
          enqueueSnackbar('Invalid percentage values')
        } else if (description === 'LEAD_LENDER_PERCENTAGE_INVALID') {
          enqueueSnackbar('The main lender should have at least 25 percent')
        } else if (description === 'EXCEEDED_LENDING_PERCENTAGE') {
          enqueueSnackbar('Total participation cannot exceed 100%')
        } else if (description === 'INVALID_FUND_AMOUNT') {
          enqueueSnackbar('Insufficient funds for one or more syndicators')
        } else {
          enqueueSnackbar(SOMETHING_WENT_WRONG)
        }
        setIsValidating(false)
      },
    }
  )

  const handleSaveAll = async () => {
    if (!participationStats.canSave) {
      if (!participationStats.hasOnlyOneLeadLender && participationStats.hasLeadLender) {
        enqueueSnackbar('Please ensure only one main lender is assigned before saving')
      } else {
        enqueueSnackbar(
          'Please ensure 100% participation and exactly one main lender before saving'
        )
      }
      return
    }

    // Check if there are any changes
    const hasChanges = JSON.stringify(lenders) !== JSON.stringify(originalLenders)

    if (!hasChanges) {
      enqueueSnackbar('No changes to save')
      return
    }

    setIsValidating(true)

    try {
      const lendersToUpdate = lenders.map(lender => ({
        syndicatorId: lender.syndicatorId,
        lendingPercentage: lender.lendingPercentage.toString(),
        lendingAmount: lender.lendingAmount.toString(),
        isLead: lender.isLead,
      }))

      const updateData: ILendersCumulativeUpdate = {
        dealId: id,
        lenders: lendersToUpdate,
      }

      await updateLendersCumulativeMut(updateData)
    } catch (error) {
      setIsValidating(false)
    }
  }

  const handleComplete = async () => {
    if (!participationStats.canSave) {
      if (!participationStats.hasOnlyOneLeadLender && participationStats.hasLeadLender) {
        enqueueSnackbar('Please ensure only one main lender is assigned before proceeding')
      } else {
        enqueueSnackbar(
          'Please ensure 100% participation and exactly one main lender before proceeding'
        )
      }
      return
    }

    // Check if there are any changes
    const hasChanges = JSON.stringify(lenders) !== JSON.stringify(originalLenders)

    // Save current state before proceeding (only if there are changes)
    if (hasChanges) {
      await handleSaveAll()
    }

    // Proceed to next step
    push(
      generatePath(NEW_DEAL_URL, {
        id,
        view: 'edit',
        type: 'inhouse',
        step: 4,
      })
    )
  }

  const dealEditView = view === 'edit'

  if (dataDealInfo && dataDealInfo.data.type === CompanyType.OutHouse)
    push(
      generatePath(DEAL_INFO_URL, {
        id: id,
      })
    )

  const alertClasses = useAlertStyles()

  return (
    <Box mt={dealEditView ? '1.625rem' : 'initial'}>
      <Paper elevation={0}>
        <Box p="3rem">
          {/* Header */}
          <Box display="flex" alignItems="center" mb={3}>
            {!dealEditView && (
              <Box mr="1.5rem">
                <IconButton color="secondary" onClick={goBack} className={cn(classes.navButton)}>
                  <ArrowLeft />
                </IconButton>
              </Box>
            )}
            <Typography variant="h2" color="textSecondary">
              Assign Lenders to Deal #{id}
            </Typography>
            <Box display="flex" alignItems="center" flex="1" justifyContent="flex-end">
              <Button
                disabled={!activeLenderActions}
                variant="text"
                color="secondary"
                startIcon={<StartIcon />}
                onClick={() =>
                  setModalAssignState({
                    type: 'new',
                    initValue: initValue,
                    open: true,
                  })
                }
              >
                Add Lender
              </Button>
            </Box>
          </Box>

          {/* Participation Summary */}
          <Box mb={3}>
            <Paper
              className={`${alertClasses.alert} ${
                participationStats.canSave ? alertClasses.success : alertClasses.warning
              }`}
              elevation={0}
            >
              <Typography variant="h6" className={alertClasses.alertTitle}>
                {participationStats.canSave ? 'Ready to Save' : 'Action Required'}
              </Typography>
              <Box>
                <Typography variant="body2" gutterBottom>
                  <strong>Total Participation:</strong>{' '}
                  {convertToPercentageFormat(participationStats.totalPercentage)}
                  {participationStats.isFullyParticipated
                    ? ' ✓'
                    : ` (${convertToPercentageFormat(
                        participationStats.availablePercentage
                      )} remaining)`}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Main Lender:</strong>{' '}
                  {participationStats.hasOnlyOneLeadLender
                    ? 'Assigned ✓  '
                    : participationStats.hasLeadLender
                    ? `Multiple (${participationStats.leadLendersCount}) - Only one allowed ✗`
                    : 'Required ✗'}
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Lenders Table */}
          <Box>
            <TableLenders columns={columns} data={lenders} loading={isLoading} />
            {popover}
          </Box>

          {/* Action Buttons */}
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveAll}
              loading={isValidating || isLoadingCumulativeUpdate}
              disabled={!participationStats.canSave}
            >
              Save All Changes
            </Button>
          </Box>
        </Box>

        {/* Modals */}
        <ModalComponent open={modalAssignState.open}>
          <Box maxWidth={300} pt={2}>
            <AssignLenderForm
              advanceAmount={
                (dataDealInfo?.data.flexAmount ?? dataDealInfo?.data.advanceAmount) || 0
              }
              fullAdvanceAmount={dataDealInfo?.data.advanceAmount || 0}
              mode={modalAssignState.type}
              initValue={modalAssignState.initValue}
              submit={modalAssignState.type === 'new' ? addLenderModal : editLenderModal}
              loading={isLoadingAddLender}
              dealId={id}
              currentTotalPercentage={participationStats.totalPercentage}
              currentLenderPercentage={
                modalAssignState.type === 'edit'
                  ? Number(modalAssignState.initValue.lendingPercentage) || 0
                  : 0
              }
              cancel={() =>
                setModalAssignState(prevState => ({
                  ...prevState,
                  open: false,
                }))
              }
            />
          </Box>
        </ModalComponent>

        <ModalComponent open={modalRemoveLenderState.open}>
          <ConfirmationContent
            isLoading={false}
            handleCancel={() =>
              setModalRemoveLenderState(prevState => ({
                ...prevState,
                open: false,
              }))
            }
            handleConfirm={() =>
              deleteLenderModal({
                companyId: modalRemoveLenderState.idCompany,
                lenderId: modalRemoveLenderState.id,
              })
            }
            text="Are you sure you want to remove this lender?"
          />
        </ModalComponent>
      </Paper>

      {/* Navigation Buttons */}
      {dealActions && (
        <Box ml="3rem" marginTop="2rem">
          <Button
            variant="contained"
            onClick={() => {
              push(
                generatePath(NEW_DEAL_URL, {
                  id,
                  view: 'edit',
                  type: 'inhouse',
                  step: 2,
                })
              )
            }}
            startIcon={<ArrowLeftIcon className={classes.icon} />}
            className={classes.button}
          >
            Back
          </Button>
          <Button
            onClick={handleComplete}
            color="primary"
            variant="contained"
            disabled={!participationStats.canSave}
            loading={isValidating || isLoadingCumulativeUpdate}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default AssignLendersContainer
